import spfetch from './spfetch';
import getTypeFromUri from './get-type-from-uri';

let rowsByUid = new Map();
const queue = [];
const episodeQueue = [];
const batchSizeLimit = 50;

const usersByUsername = new Map();
export async function fetchUserByUsername(username) {
  if (usersByUsername.has(username)) return usersByUsername.get(username);

  const response = await spfetch(
    `https://api.spotify.com/v1/users/${encodeURIComponent(username)}`
  );
  const {
    id,
    display_name: name = id,
    images: [{ url: imageUrl } = {}] = [],
  } = response;
  const user = { name, imageUrl, response };
  usersByUsername.set(username, user);
  return user;
}

export default async function decorate({
  uri,
  uid = uri,
  metadata = {},
  provider,
}) {
  if (rowsByUid.has(uid)) return rowsByUid.get(uid);
  if (!/^spotify:(track|ad|episode):/.test(uri)) return null;

  const queuedBy =
    metadata &&
    metadata.queued_by &&
    (await fetchUserByUsername(metadata.queued_by));

  if (
    (metadata && metadata.title && metadata.image_small_url) ||
    metadata.is_advertisement === 'true'
  ) {
    const row = {
      uid,
      provider,
      queuedBy,
      metadata,
      track: {
        uri: uri,
        name: metadata.title,
        album: {
          name: metadata.album_title,
          images: [
            {
              url:
                (metadata.image_large_url || '').replace(
                  /^spotify:image:([^:]+)$/,
                  'https://i.scdn.co/image/$1'
                ) || null,
            },
            {
              url:
                (metadata.image_url || '').replace(
                  /^spotify:image:([^:]+)$/,
                  'https://i.scdn.co/image/$1'
                ) || null,
            },
            {
              url:
                (metadata.image_small_url || '').replace(
                  /^spotify:image:([^:]+)$/,
                  'https://i.scdn.co/image/$1'
                ) || null,
            },
          ],
        },
        artists: Object.keys(metadata)
          .filter((key) => key.startsWith('artist_name'))
          .map((artistNameKey) => {
            const uri = metadata[artistNameKey.replace('name', 'uri')];
            return { name: metadata[artistNameKey], uri, id: uri?.substr(-22) };
          }),
      },
    };
    rowsByUid.set(uid, row);
    return row;
  }

  let resolve;
  let reject;
  // Steal `resolve` and `reject` function from promise to be control it from
  // the outside when eg. batching
  const promise = new Promise(
    (_resolve, _reject) => ((resolve = _resolve), (reject = _reject))
  );
  promise.resolve = resolve;
  promise.reject = reject;
  rowsByUid.set(uid, promise);

  const type = getTypeFromUri(uri);
  if (type === 'track') {
    // These are batchable, so if this was the uri of its kind, set of a timeout
    // which will gather all uris of this type and look them up in maxium batch
    // sizes in the next tick.
    if (queue.push({ uri, uid, provider, metadata }) === 1) {
      setTimeout(function takeBatch() {
        const batch = queue.splice(0, batchSizeLimit);
        if (!batch.length) return;

        spfetch(
          `https://api.spotify.com/v1/tracks?ids=${batch.map(({ uri }) =>
            uri.substr(-22)
          )}`
        ).then(({ tracks }) => {
          tracks.forEach((track, i) => {
            const { uid, provider, metadata } = batch[i];

            const row = {
              uid,
              provider,
              queuedBy,
              track,
              metadata,
            };
            const resolve = rowsByUid.get(uid).resolve;
            rowsByUid.set(uid, row);
            if (resolve) resolve(row);
          });

          takeBatch();
        });
      });
    }
  } else if (type === 'episode') {
    // These are batchable, so if this was the uri of its kind, set of a timeout
    // which will gather all uris of this type and look them up in maxium batch
    // sizes in the next tick.
    if (episodeQueue.push({ uri, uid, provider, metadata }) === 1) {
      setTimeout(function takeBatch() {
        const batch = episodeQueue.splice(0, batchSizeLimit);
        if (!batch.length) return;

        spfetch(
          `https://api.spotify.com/v1/episodes?ids=${batch.map(({ uri }) =>
            uri.substr(-22)
          )}`
        ).then(({ episodes }) => {
          episodes.forEach((track, i) => {
            const { uid, provider, metadata } = batch[i];

            const row = {
              uid,
              provider,
              queuedBy,
              track,
              metadata,
            };
            const resolve = rowsByUid.get(uid).resolve;
            rowsByUid.set(uid, row);
            if (resolve) resolve(row);
          });

          takeBatch();
        });
      });
    }
  }

  return promise;
}
